import { useState } from "react";
import {
  Button,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
} from "@salestrak/ui";
import {
  useReasons,
  useReasonMutations,
  type Reason,
} from "#app/hooks/settings/use-reason-mutations";
import { normalizeOutcomeTone, type OutcomeTone } from "#app/lib/outcome-tone";
import { usePermission } from "#app/lib/hooks/use-permission";
import { DEFAULT_META_LABEL } from "#app/components/entity";
import { SettingsGroup, SettingsGroupRow } from "./settings-group";
import { SettingsGroupRowsSkeleton } from "./settings-page-skeleton";
import { OutcomeFormDialog } from "./outcome-form-dialog";
import { OutcomeToneDot } from "./outcome-tone-dot";
import { OutcomeToneSections } from "./outcome-tone-sections";
import { TrashConfirmDialog } from "./trash-confirm-dialog";

/**
 * The Project Update outcome vocabulary — the server `Reason` pool, tone-
 * sectioned. Projects carry the full lifecycle a server-backed pool allows:
 * an outcome can be disabled without deleting it (past Updates still name it),
 * and one can be the default the Update dialog preselects.
 */
export function UpdateOutcomesGroup() {
  // Without the manage key the vocabulary is a read-only reference: the tone
  // sections list with no `+` and no row menus.
  const canManage = usePermission("reasons.manage");
  const { reasons, isLoading } = useReasons();
  const {
    createReason,
    updateReason,
    toggleReason,
    deleteReason,
    setDefaultReason,
    clearDefaultReason,
  } = useReasonMutations();

  const [createTone, setCreateTone] = useState<OutcomeTone | null>(null);
  const [editing, setEditing] = useState<Reason | null>(null);
  const [deleting, setDeleting] = useState<Reason | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  return (
    <>
      {/* Headerless: the page title already says "Outcomes" and the tab
          names the entity, so a second label over the card doubled up. */}
      <SettingsGroup>
        {isLoading && reasons.length === 0 ? (
          <SettingsGroupRowsSkeleton rows={3} />
        ) : (
          <OutcomeToneSections
            items={reasons}
            toneOf={(reason) => normalizeOutcomeTone(reason.tone)}
            keyOf={(reason) => reason.reasonId}
            onAdd={canManage ? setCreateTone : undefined}
          labelOf={(reason) => reason.name}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
            renderRow={(reason) => (
              <SettingsGroupRow
                label={reason.name}
                // A disabled outcome is no longer the default, whatever its
                // flag says, so the meta line states one fact or the other.
                subtitle={
                  reason.archived
                    ? "Disabled"
                    : reason.isDefault
                      ? DEFAULT_META_LABEL
                      : undefined
                }
                leading={
                  <OutcomeToneDot tone={normalizeOutcomeTone(reason.tone)} />
                }
                actionsVisible={openMenuId === reason.reasonId}
                hoverActions
              >
                {canManage ? (
                <Menu
                  onOpenChange={(open) =>
                    setOpenMenuId(open ? reason.reasonId : null)
                  }
                >
                  <MenuTrigger
                    render={
                      <Button
                        aria-label={`Actions for ${reason.name}`}
                        variant="ghost"
                        size="icon-xs"
                      />
                    }
                  >
                    <Icon name="ellipsis" size="md" />
                  </MenuTrigger>
                  <MenuPopup align="end" className="min-w-45">
                    <MenuItem onClick={() => toggleReason(reason)}>
                      <Icon name="eye-closed" size="md" />
                      {reason.archived ? "Enable outcome" : "Disable outcome"}
                    </MenuItem>
                    <MenuItem onClick={() => setEditing(reason)}>
                      <Icon name="pen-line" size="md" />
                      Edit
                    </MenuItem>
                    {!reason.archived && !reason.isDefault ? (
                      <MenuItem onClick={() => setDefaultReason(reason.id)}>
                        <Icon name="check" size="md" />
                        Set as default
                      </MenuItem>
                    ) : null}
                    {!reason.archived && reason.isDefault ? (
                      <MenuItem onClick={() => clearDefaultReason()}>
                        <Icon name="x-circle" size="md" />
                        Clear as default
                      </MenuItem>
                    ) : null}
                    <MenuSeparator />
                    <MenuItem
                      onClick={() => setDeleting(reason)}
                    >
                      <Icon name="trash" size="md" />
                      Delete...
                    </MenuItem>
                  </MenuPopup>
                </Menu>
                ) : null}
              </SettingsGroupRow>
            )}
          />
        )}
      </SettingsGroup>

      <OutcomeFormDialog
        open={createTone != null}
        onOpenChange={(open) => {
          if (!open) setCreateTone(null);
        }}
        initialTone={createTone ?? undefined}
        createSubtitle="For Project Updates."
        onSubmit={async ({ name, tone }) => {
          // A refusal (a taken name) is toasted by the hook; rejecting keeps
          // the dialog open on the typed name.
          if (!(await createReason(name, tone))) throw new Error("not created");
        }}
      />

      <OutcomeFormDialog
        open={!!editing}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        initialName={editing?.name}
        initialTone={
          editing ? normalizeOutcomeTone(editing.tone) : undefined
        }
        onSubmit={async ({ name, tone }) => {
          if (editing && !(await updateReason(editing.id, name, tone))) {
            throw new Error("not updated");
          }
        }}
      />

      <TrashConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title={`Delete "${deleting?.name ?? ""}"?`}
        description="Projects lose this outcome. Past Updates keep the name they were posted with."
        confirmLabel="Delete"
        onConfirm={async () => {
          if (deleting) await deleteReason(deleting);
        }}
      />
    </>
  );
}
